import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  AlertTriangle, CheckCircle2, Copy, Eye, EyeOff, Key, KeyRound,
  RefreshCw, ShieldCheck, Trash2, UserPlus,
} from "lucide-react";
import { API } from "../../lib/api";
import { ROLE_LABELS } from "../../lib/dashConstants";

const INITIALS = (s) => (s || "U").trim().slice(0, 2).toUpperCase();

export default function UserManagement() {
  const [data, setData] = useState({ users: [], roles: [], role_labels: {} });
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ username: "", role: "sales", name: "", password: "",
                                       team: "", birthdate: "", employee_id: "", email: "" });
  const [feedback, setFeedback] = useState(null);
  const [showPw, setShowPw] = useState({}); // map username → bool

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auth/admin/users`);
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (msg, ok = true) => {
    setFeedback({ ok, msg });
    setTimeout(() => setFeedback(null), 6000);
  };

  const createUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      const r = await axios.post(`${API}/auth/admin/users`, payload);
      flash(`User ${r.data.username} dibuat. Password dikirim ke email admin.`);
      setCreateOpen(false);
      setForm({ username: "", role: "sales", name: "", password: "",
                team: "", birthdate: "", employee_id: "", email: "" });
      load();
    } catch (e) {
      flash(e?.response?.data?.detail || "Gagal membuat user", false);
    } finally { setSubmitting(false); }
  };

  const resetPassword = async (username) => {
    if (!window.confirm(`Reset password untuk ${username}? Password baru akan dikirim ke email admin.`)) return;
    try {
      await axios.post(`${API}/auth/admin/users/reset-password`, { username });
      flash(`Password ${username} di-reset. Cek email admin.`);
    } catch (e) {
      flash(e?.response?.data?.detail || "Gagal reset", false);
    }
  };

  const disableUser = async (username) => {
    if (!window.confirm(`HAPUS PERMANEN akun ${username}? Tindakan ini tidak bisa dibatalkan dan akun akan hilang seluruhnya dari sistem.`)) return;
    try {
      await axios.delete(`${API}/auth/admin/users/${username}`);
      flash(`✅ ${username} telah dihapus dari sistem.`);
      load();
    } catch (e) {
      flash(e?.response?.data?.detail || "Gagal menghapus", false);
    }
  };

  const copyToClipboard = (text, label) => {
    try {
      navigator.clipboard.writeText(text);
      flash(`📋 ${label} disalin ke clipboard`);
    } catch {
      flash("Gagal menyalin", false);
    }
  };

  const toggleEditor = async (username, key, currentValue) => {
    try {
      await axios.post(`${API}/auth/admin/users/permission-toggle`, {
        username, permission_key: key, value: !currentValue,
      });
      flash(`Editor ${key.replace("editor_", "")} untuk @${username}: ${!currentValue ? "ON" : "OFF"}`);
      load();
    } catch (e) {
      flash(e?.response?.data?.detail || "Gagal", false);
    }
  };

  const isEditor = (u, key) => {
    // Admin/Manager always editor
    if (u.role === "admin" || u.role === "manager") return true;
    return !!(u.permissions_override || {})[key];
  };

  return (
    <div className="users-card" data-testid="users-management">
      <div className="card-head" style={{flexWrap: "wrap", gap: 12}}>
        <div>
          <h3 className="card-title">User Management</h3>
          <p className="card-subtitle">
            Kelola identitas, role, dan akses. Setiap perubahan password otomatis dikirim ke email admin.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setCreateOpen(!createOpen)} data-testid="btn-create-user">
          <UserPlus size={14}/> {createOpen ? "Tutup" : "Buat User Baru"}
        </button>
      </div>

      {feedback && (
        <div className={`alert ${feedback.ok ? "ok" : "err"}`} style={{margin: "12px 0"}} data-testid="user-feedback">
          {feedback.ok ? <CheckCircle2 size={14}/> : <AlertTriangle size={14}/>} {feedback.msg}
        </div>
      )}

      {createOpen && (
        <form onSubmit={createUser} className="card" style={{marginTop: 14, padding: 18, background: "var(--bg-2)"}} data-testid="create-user-form">
          <h4 style={{margin: "0 0 12px 0", color: "var(--navy)"}}>Buat User Baru</h4>
          <div className="entry-grid">
            <div className="field">
              <label>Username *</label>
              <input className="select-input" required minLength={3} maxLength={64}
                value={form.username} onChange={(e) => setForm({...form, username: e.target.value})}
                data-testid="new-user-username" />
            </div>
            <div className="field">
              <label>Role *</label>
              <select className="select-input" value={form.role}
                onChange={(e) => setForm({...form, role: e.target.value})} data-testid="new-user-role">
                {(data.roles || []).map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Nama Lengkap</label>
              <input className="select-input" maxLength={120} value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})} data-testid="new-user-name" />
            </div>
            <div className="field">
              <label>Password (kosongkan untuk auto-generate)</label>
              <input className="select-input" type="text" maxLength={200} value={form.password}
                onChange={(e) => setForm({...form, password: e.target.value})}
                placeholder="Auto-generate (rekomendasi)" data-testid="new-user-password" />
            </div>
            {(form.role === "sales" || form.role === "sales_tl") && (
              <div className="field">
                <label>Team {form.role === "sales" ? "*" : "(opsional)"}</label>
                <select className="select-input" value={form.team}
                  onChange={(e) => setForm({...form, team: e.target.value})} data-testid="new-user-team">
                  <option value="">— pilih team —</option>
                  <option value="ROYAL">ROYAL</option>
                  <option value="DIAMOND">DIAMOND</option>
                </select>
              </div>
            )}
            <div className="field">
              <label>Email</label>
              <input className="select-input" type="email" maxLength={200} value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})} data-testid="new-user-email" />
            </div>
            <div className="field">
              <label>Tanggal Lahir</label>
              <input className="select-input" type="date" value={form.birthdate}
                onChange={(e) => setForm({...form, birthdate: e.target.value})} data-testid="new-user-birthdate" />
            </div>
            <div className="field">
              <label>Nomor Induk Karyawan</label>
              <input className="select-input" maxLength={64} value={form.employee_id}
                onChange={(e) => setForm({...form, employee_id: e.target.value})} data-testid="new-user-empid" />
            </div>
          </div>
          <div style={{display: "flex", gap: 10, marginTop: 12}}>
            <button type="button" className="btn btn-secondary" onClick={() => setCreateOpen(false)}>Batal</button>
            <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="submit-create-user">
              {submitting ? <RefreshCw size={14} className="spin"/> : <UserPlus size={14}/>} Buat & Kirim Email
            </button>
          </div>
        </form>
      )}

      <div className="users-grid">
        {loading ? <div className="empty">Memuat user...</div> :
          data.users.map((u) => {
            const pwVisible = !!showPw[u.username];
            const plainPw = u.plaintext_password || "—";
            return (
            <div key={u.username} className={`user-row ${u.active === false ? "disabled" : ""}`} data-testid={`user-row-${u.username}`}>
              <div className="user-avatar">{INITIALS(u.username)}</div>
              <div className="user-meta">
                <b>{u.name || u.username}</b>
                <span className={`role-pill role-${u.role}`}>{ROLE_LABELS[u.role] || u.role}</span>
                {u.team && <span className="badge" style={{background: u.team === "DIAMOND" ? "#c40303" : "#041f60", color: "#fff", fontSize: 10}}>{u.team}</span>}
                <span className="sub">@{u.username}</span>
                {/* Password reveal/copy row */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 11 }}>
                  <KeyRound size={11} color="var(--warn)" />
                  <span style={{ color: "var(--muted)" }}>Password:</span>
                  <code data-testid={`pw-display-${u.username}`}
                    style={{ background: "var(--surface-2)", padding: "2px 8px", borderRadius: 4,
                              fontFamily: "monospace", fontSize: 11, letterSpacing: pwVisible ? 0 : 4, minWidth: 110,
                              border: "1px solid var(--border-2)", color: "var(--navy)" }}>
                    {pwVisible ? plainPw : "•".repeat(Math.min(plainPw.length, 12))}
                  </code>
                  <button className="icon-btn" title={pwVisible ? "Sembunyikan" : "Tampilkan"}
                    onClick={() => setShowPw({ ...showPw, [u.username]: !pwVisible })}
                    data-testid={`pw-toggle-${u.username}`}
                    style={{ padding: 2, color: "var(--muted)" }}>
                    {pwVisible ? <EyeOff size={12} /> : <Eye size={12} />}
                  </button>
                  <button className="icon-btn" title="Salin password"
                    onClick={() => copyToClipboard(plainPw, `Password @${u.username}`)}
                    data-testid={`pw-copy-${u.username}`}
                    style={{ padding: 2, color: "var(--accent)" }}>
                    <Copy size={12} />
                  </button>
                </div>
              </div>
              <div style={{display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap"}}>
                {/* Editor permission toggles — only for non-admin/manager */}
                {u.role !== "admin" && u.role !== "manager" && (
                  <>
                    {["leads", "accounts", "churn"].map((k) => {
                      const key = `editor_${k}`;
                      const on = isEditor(u, key);
                      return (
                        <button key={k} className="icon-btn"
                          title={`Editor ${k}: ${on ? "ON" : "OFF"} (klik toggle)`}
                          onClick={() => toggleEditor(u.username, key, on)}
                          data-testid={`perm-${k}-${u.username}`}
                          style={{ fontSize: 10, padding: "3px 7px", borderRadius: 4,
                                    background: on ? "var(--good)" : "var(--surface-2)",
                                    color: on ? "#fff" : "var(--muted)",
                                    border: "1px solid var(--border-2)" }}>
                          <ShieldCheck size={10} style={{ verticalAlign: -1, marginRight: 2 }} />{k}
                        </button>
                      );
                    })}
                  </>
                )}
                <button className="icon-btn" title="Reset Password (auto-generate baru)"
                  onClick={() => resetPassword(u.username)}
                  data-testid={`reset-pw-${u.username}`} style={{color: "var(--warn)"}}>
                  <Key size={14}/>
                </button>
                <button className="icon-btn" title="HAPUS PERMANEN akun ini"
                  onClick={() => disableUser(u.username)}
                  data-testid={`delete-${u.username}`}
                  style={{color: "#fff", background: "var(--bad)", padding: "4px 7px", borderRadius: 4}}>
                  <Trash2 size={13}/>
                </button>
              </div>
            </div>
          );})}
      </div>
    </div>
  );
}
